import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
// DatePicker work just with moment
import * as moment from 'moment'
import { ConfigProvider, DatePicker } from 'antd'
import { ResourceTasksTable } from '@mercadoni/gungnir'
import { observer } from 'mobx-react-lite'
import { useResourceProfileStore } from './resource-profile.context'
import MultiCheckSelect from '@commonComponents/MultiCheckSelect'
import JobModal from '@/shared/job-modal'
import { getJobByUUID } from '@/domain/jobs/jobs.services'
import { getWeekFromTo, getTasksStatus, getStates, getFromTo } from './resource-profile.tools'
import './resource-profile.styles.less'
import { getLocale } from '../../refactory/utils/antd'
import { getFormattedDate, FULL_DATE_FORMAT } from '../../refactory/utils/dates'

const { RangePicker } = DatePicker

const RANGES_CONFIG = {
  Today: [moment(), moment()],
  'This Week': [moment().subtract(1, 'week'), moment()],
  'This Month': [moment().startOf('month'), moment().endOf('month')],
  'Last Month': [moment().subtract(1, 'month'), moment()]
}

const TASKS_STATUS = ['FINISHED', 'RE_ALLOCATED', 'RESCHEDULED', 'TASK_RESET', 'CANCELLED']

const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_PAGINATION = {
  current: 1,
  defaultPageSize: 50,
  pageSize: 20,
  showSizeChanger: false
}

const HistoricTab = (props) => {
  const { resourceId, activeTab, clientTimeZone, scrollY } = props
  const { fromDate, toDate } = getWeekFromTo(dayjs())
  const { t } = useTranslation()
  const store = useResourceProfileStore()
  const [tasksStatusSelected, setTaskStatusSelected] = useState([])
  const [rangeDates, setRangeDates] = useState([
    moment(fromDate.format(DATE_FORMAT)),
    moment(toDate.format(DATE_FORMAT))
  ])
  const [selectedJob, setSelectedJob] = useState(null)
  const [showJobModal, setShowJobModal] = useState(false)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const [shouldClose, setShouldClose] = useState(false)

  const columnNames = [
    {
      dataIndex: 'job_number',
      title: t('jobNumber')
    },
    {
      dataIndex: 'task_type',
      title: t('tasks'),
      render: (record) => t(record),
      width: 150
    },
    {
      title: t('date'),
      dataIndex: 'created_date',
      width: 110
    },
    {
      title: t('status'),
      dataIndex: 'status'
    }
  ]

  const handleModal = () => {
    setShowJobModal(!showJobModal)
  }

  const onClickReference = async (jobUuid) => {
    const completeJob = await getJobByUUID(jobUuid)
    if (completeJob) {
      setSelectedJob(completeJob)
      handleModal()
    }
  }

  const getHistoricTasks = async (dates) => {
    const { pageSize } = pagination
    const [from, to] = dates || rangeDates
    await store.loadHistoricTasks(resourceId, {
      limit: pageSize,
      offset: 0,
      ...getFromTo(from, to, clientTimeZone),
      states: getStates(tasksStatusSelected)
    })
    setPagination({
      ...pagination,
      current: 1
    })
  }

  const onRangePickerChange = (dates) => {
    setRangeDates(dates)
    getHistoricTasks(dates)
  }

  const searchTasksByStatus = () => {
    getHistoricTasks()
  }

  const resourceTasksTableProps = {
    columnNames,
    copy: t,
    onTableChange: async (paginationChanged) => {
      const [from, to] = rangeDates
      const { current, pageSize, total } = paginationChanged
      const payload = {
        ...getFromTo(from, to, clientTimeZone),
        limit: pageSize,
        offset: (current - 1) * pageSize,
        states: getStates(tasksStatusSelected)
      }
      await store.loadHistoricTasks(resourceId, payload)
      setPagination({
        ...pagination,
        total,
        current
      })
    },
    pagination,
    assignedTasks: false,
    onClickReference
  }

  if (store.historicTasks) {
    resourceTasksTableProps.tasks = store.historicTasks.tasks.map((task) => {
      return {
        ...task,
        state: getTasksStatus(task.state),
        created_date: getFormattedDate({ date: task.created_at, format: FULL_DATE_FORMAT, timezoneIn: clientTimeZone })
      }
    })
    resourceTasksTableProps.pagination = {
      ...pagination,
      total: store.historicTasks.number_of_pages * pagination.pageSize
    }
  }

  const initialize = async (from, to) => {
    const { pageSize } = pagination
    await store.loadHistoricTasks(resourceId, {
      limit: pageSize,
      offset: 0,
      ...getFromTo(from, to, clientTimeZone)
    })
  }

  useEffect(() => {
    const [from, to] = [moment(fromDate.format(DATE_FORMAT)), moment(toDate.format(DATE_FORMAT))]

    if (activeTab === '1') {
      initialize(from, to)
    }

    return () => {
      setTaskStatusSelected([])
      setRangeDates([from, to])
      setShowJobModal(false)
      setPagination(DEFAULT_PAGINATION)
      setShouldClose(true)
    }
  }, [resourceId, activeTab])

  useEffect(() => {
    if (scrollY > 0 && !shouldClose) {
      setShouldClose(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY])

  return (
    <div className='assigned-quotas-tab'>
      <div className='assigned-quotas-tab-title'>{t('historyOfAssignedTasks')}</div>
      <div className='assigned-quotas-tab-header-actions'>
        <div className='left-actions' />
        <div className='right-actions'>
          <MultiCheckSelect
            saveText={t('ready')}
            allText={t('all')}
            buttonText={t('statuses')}
            checkListTitle={t('allTheStatuses')}
            onChangeSelected={searchTasksByStatus}
            setDataSelected={setTaskStatusSelected}
            dataSelected={tasksStatusSelected}
            dataSource={TASKS_STATUS.map((state) => {
              return { label: t(getTasksStatus(state)), value: state }
            })}
            shouldClose={shouldClose}
            setShouldClose={setShouldClose}
          />
          <ConfigProvider locale={getLocale()}>
            <RangePicker
              id='search-by-job-created'
              allowClear={false}
              value={rangeDates}
              className='filters-fields'
              size='small'
              onChange={onRangePickerChange}
              ranges={RANGES_CONFIG}
            />
          </ConfigProvider>
        </div>
      </div>
      <ResourceTasksTable {...resourceTasksTableProps} />
      <JobModal
        {...props}
        visible={showJobModal}
        handleModal={handleModal}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        getJobsList={() => {}}
      />
    </div>
  )
}

export default observer(HistoricTab)
